import { useCallback, useEffect, useState } from 'react';
import { ProductsSortBy, SearchProductsRequest } from '../../../clients/quipli-service/product/product';
import { useSearchParams } from 'react-router-dom';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { PaginationData, PaginationRequest } from '../../../clients/quipli-service/pagination/pagination';
import { toast } from '../../../util/toasts';
import Container from '../../../components/container/container';
import { ProductCard } from '../product-card';
import { FilterGroup } from '../../../clients/customer-api/category-filters/category-filters';
import { Label } from '../../../clients/quipli-service/label/label';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { Modal } from '../../../components/modal/modal';
import { Button } from '../../../components/form/button';
import { EcommerceUser } from '../product-details/product-details';
import { CustomerProduct } from '../../../clients/customer-api/product/customer-product';
import { Location } from '../../../clients/quipli-service/location/location';
import { useCustomerAPI } from '../../../clients/customer-api/customer-api-client';
import { Typography } from '../../../components/typography/typography';
import { Category } from '../../../clients/quipli-service/category/category';
import { SelectForm } from '../../../components/select-form/select-form';
import { Configuration } from '../../../clients/quipli-service/configuration/configuration';
import { Spinner } from '../../../components/spinner/spinner';
import { UrlAliasType } from '../../../clients/customer-api/url-alias/url-alias';
import { Dictionary, keyBy } from 'lodash';
import './search-results.scss';
import classNames from 'classnames';
import { CalculateSalePriceResult, RentalProductPrice } from '../../../clients/quipli-service/pricing/pricing';
import { OrderType } from '../../../clients/quipli-service/order/order';

function extractInvoiceSearchParams(params: URLSearchParams): SearchProductsRequest {
  return {
    filterIds: params.has('filterIds')
      ? params
          .get('filterIds')
          ?.split(',')
          .map((f) => parseInt(f))
      : undefined,
    sortBy: params.get('sortBy') || 'relevance',
    sortDirection: params.get('sortDirection') || 'desc',
    pageNumber: params.get('pageNumber') ? parseInt(params.get('pageNumber')!) : undefined,
    categoryIds: params.has('categoryIds') ? [params.get('categoryIds')] : undefined,
  } as SearchProductsRequest;
}

export const ProductSearchResults = ({
  user,
  locationId,
  keyword,
  categoryId,
}: {
  user: EcommerceUser;
  locationId?: number;
  keyword?: string;
  categoryId?: number;
}) => {
  const {
    searchProducts,
    getConfigurations,
    getLocationById,
    getUrlAliases,
    getMultipleLabels,
    getMultipleCategoriesByIds,
    getCategoryFilters,
    listPricesForMultipleProducts,
    calculatePrices,
  } = useCustomerAPI();

  const [results, setResults] = useState<CustomerProduct[]>(), // add products each time new page is loaded
    [pagination, setPagination] = useState<PaginationData>(),
    [labels, setLabels] = useState<Dictionary<Label>>(),
    [configs, setConfigs] = useState<Dictionary<Configuration>>(),
    [loading, setLoading] = useState<boolean>(true),
    [category, setCategory] = useState<Category>(),
    [location, setLocation] = useState<Location & { urlAlias: string }>(),
    [categoryFilters, setCategoryFilters] = useState<FilterGroup[]>([]),
    [filtersModalOpen, setFiltersModalOpen] = useState<boolean>(false),
    [salePrices, setSalePrices] = useState<Dictionary<CalculateSalePriceResult>>({}),
    [rentalPrices, setRentalPrices] = useState<Map<number, RentalProductPrice[]>>(new Map());

  const search = useCallback((_request: SearchProductsRequest) => {
    setLoading(true);

    searchProducts({
      ..._request,
      pageSize: parseInt(configs ? configs['CONF_DEF_ITEMS_PER_PAGE_CATALOG']?.value ?? '30' : '30'),
    })
      .then(({ items, pagination }) => {
        setResults((results ?? []).concat(items));
        setPagination(pagination);

        const saleItems = items.filter((item) => item.allowSale);
        const rentalItems = items.filter((item) => item.allowRent);

        Promise.all([
          saleItems.length
            ? calculatePrices(
                saleItems.map((item) => ({
                  productId: item.id,
                  locationId: locationId,
                  type: OrderType.Sale,
                  userId: user.userId,
                })),
              )
            : [],
          rentalItems.length
            ? listPricesForMultipleProducts(
                rentalItems.map((item) => item.id),
                locationId,
                OrderType.Rental,
                user.userId,
              )
            : new Map(),
        ]).then(([_salePrices, _rentalPrices]) => {
          setSalePrices(keyBy(_salePrices as CalculateSalePriceResult[], 'productId'));
          setRentalPrices(_rentalPrices as Map<number, RentalProductPrice[]>);
          setLoading(false);
        });
      })
      .catch((e) => {
        toast(e.message, 'error');
        setLoading(false);
      });
  }, []);

  // List searchRequest hooks
  const [params] = useSearchParams();
  const { request: searchRequest, setRequest: setSearchRequest } = useRequestQueryParams<SearchProductsRequest>(
    {
      ...extractInvoiceSearchParams(params),
      pageSize: configs?.['CONF_DEF_ITEMS_PER_PAGE_CATALOG']
        ? parseInt(configs?.['CONF_DEF_ITEMS_PER_PAGE_CATALOG']?.value)
        : 30,
      keyword,
      categoryIds: categoryId ? [categoryId] : undefined,
      locationIds: locationId ? [locationId] : undefined,
    },
    search,
  );

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const [_configs, _labels, _category] = await Promise.all([
          getConfigurations([
            'CONF_DEF_ITEMS_PER_PAGE_CATALOG',
            'CONF_WEBSITE_NAME',
            'CONF_USER_UNAPPROVED_HIDE_PRICES',
            'CONF_CURRENCY',
            'CONF_ECOMMERCE_SALE_REQUIRE_IN_STOCK',
            'CONF_SHOW_PRICES_WITH_TAX',
            'CONF_PRICE_TAX_LABEL',
          ]),
          getMultipleLabels([
            'L_Search_Results',
            'L_Sort_By',
            'L_ITEMS',
            'L_Relevance',
            'L_BUILD_PRODUCT',
            'L_Out_of_Stock',
            'L_We_could_not_find_matches',
          ]),
          categoryId
            ? getMultipleCategoriesByIds([categoryId]).then((categories) => categories[0])
            : Promise.resolve(undefined),
        ]);
        const keyedConfigs = keyBy(_configs, 'variable');
        setConfigs(keyedConfigs);
        setLabels(keyBy(_labels, 'key'));
        setCategory(_category);
        if (_category) {
          const filters = await getCategoryFilters(_category.id);
          setCategoryFilters(filters);
        }
        if (categoryId && !_category) {
          toast('Category not found', 'error');
          return;
        }
        setSearchRequest({
          ...searchRequest,
          categoryIds: _category ? [_category.id] : undefined,
          pageSize: parseInt(keyedConfigs['CONF_DEF_ITEMS_PER_PAGE_CATALOG']?.value ?? '30'),
          sortBy: 'relevance',
        });
      } catch (e) {
        toast('Oops! Something went wrong.', 'error');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    const refreshLocation = async () => {
      if (locationId) {
        setLoading(true);
        const [location, alias] = await Promise.all([
          getLocationById(locationId),
          getUrlAliases(UrlAliasType.Shop, [locationId]),
        ]);
        setLocation({ ...location, urlAlias: alias[0].alias });
        setLoading(false);
      }
    };
    refreshLocation();
  }, [locationId]);

  const sortByOptions: { label: string; value: ProductsSortBy }[] = [
    { label: (labels && labels['L_Relevance']?.caption) ?? 'Relevance', value: 'relevance' },
    { label: 'Most Recent', value: 'createdAt' },
    { label: 'Featured', value: 'featured' },
  ];

  return !loading && labels && configs && results && pagination && salePrices ? (
    <div className="search-results">
      <div className="cols-for-filters-and-results md:grid md:grid-cols-4 md:gap-4">
        {/* Filters sidebar */}
        {category && categoryFilters?.length > 0 && (
          <div>
            <Container className="filters-sidebar col-span-1 sm-hide sticky top-[100px] border-[1px] border-solid border-gray-300 rounded-md py-2 px-4">
              <FiltersContent
                categoryFilters={categoryFilters}
                appliedFilters={searchRequest.filterIds || []}
                setAppliedFilters={(filterIds) => {
                  setSearchRequest({ ...searchRequest, filterIds });
                }}
              />
            </Container>
          </div>
        )}

        {/* Results section */}
        <div className={`md:${categoryFilters?.length > 0 ? 'col-span-3' : 'col-span-4'}`}>
          {category?.name && (
            <div className="mb-3">
              <Typography type="display-sm">{category.name}</Typography>
            </div>
          )}

          {/* Sort by dropdown and results count */}
          <div
            id="results-sort-by-row"
            className={`flex justify-between items-baseline ${
              categoryFilters?.length > 0 ? 'md:col-span-3' : 'md:col-span-4'
            }`}
          >
            {/* Sort by */}
            <div className="mb-2 flex gap-1 items-center">
              <span className="min-w-fit text-sm">{labels['L_Sort_By']?.caption ?? 'Sort By'}:</span>
              <SelectForm
                className=""
                options={sortByOptions}
                onChange={(c) => {
                  setSearchRequest((prevState) => ({ ...prevState, sortBy: c!.value }));
                }}
                getOptionLabel={(o) => o.label}
                getOptionValue={(o) => `${o.value}`}
                isClearable={false}
                value={
                  searchRequest.sortBy ? sortByOptions.find((c) => c.value === searchRequest.sortBy) : sortByOptions[0]
                }
              />
            </div>

            {/* Filters button (small screens only) */}
            {category && categoryFilters?.length > 0 && (
              <div className="block md-hide">
                <Button className="" onClick={() => setFiltersModalOpen(true)}>
                  Filters
                </Button>
              </div>
            )}

            {/*  Search results total */}
            <div className="md:block bg-gray-300 p-2 min-w-fit sm-hide">
              <Typography type="text-sm">
                {labels['L_Search_Results']?.caption ?? 'Search Results'} - {pagination?.total ?? 0}{' '}
                {labels['L_ITEMS']?.caption ?? 'items'}
              </Typography>
            </div>
          </div>
          <div className="md-hide bg-gray-300 p-2 min-w-fit mb-2">
            <Typography type="text-sm">
              {labels['L_Search_Results']?.caption ?? 'Search Results'} - {pagination?.total ?? 0}{' '}
              {labels['L_ITEMS']?.caption ?? 'items'}
            </Typography>
          </div>

          {/* category text */}
          {category && category.topText && (
            <div className={`mb-2 ${categoryFilters?.length > 0 ? 'md:col-span-3' : 'md:col-span-4'}`}>
              <div
                className=" text-md"
                dangerouslySetInnerHTML={{
                  __html:
                    category.topText
                      .replaceAll('{company}', configs['CONF_WEBSITE_NAME']?.value ?? '')
                      .replaceAll('{category}', category.name)
                      .replaceAll('{location}', location?.name ?? '') || '',
                }}
              ></div>
            </div>
          )}

          {/* Products */}
          <div
            className={` w-full justify-self-center mt-8 ${
              category && categoryFilters?.length ? 'md:col-span-3' : 'md:col-span-4'
            }`}
          >
            <div
              id="products-list"
              className="result__items products-list grid md:grid-cols-2 lg:grid-cols-3 gap-7 w-full"
            >
              {results.length === 0 ? (
                <div className="justify-self-center">
                  <img src={`https://quipli-service-prod-public.s3.amazonaws.com/images/binoculars.svg`} />
                  <Typography type="text-md">
                    {labels['L_We_could_not_find_matches']?.caption ?? 'No results found'}
                  </Typography>
                </div>
              ) : (
                <>
                  {results.map((product) => (
                    <div className={`result__items-item h-full justify-self-center`} key={product.id}>
                      <ProductCard
                        product={product}
                        key={product.id}
                        location={location}
                        isLoggedIn={user.isLoggedIn}
                        labels={labels}
                        configs={configs}
                        rentalPrices={rentalPrices.get(product.id)}
                        salePrice={salePrices[product.id]?.totalPrice}
                        saleTax={salePrices[product.id]?.taxPercentage}
                      />
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filters modal */}
      <Modal isOpen={filtersModalOpen} close={() => setFiltersModalOpen(false)}>
        <FiltersContent
          categoryFilters={categoryFilters}
          appliedFilters={searchRequest.filterIds || []}
          setAppliedFilters={(filterIds) => {
            setSearchRequest({ ...searchRequest, filterIds });
          }}
        />
        <Button className="" onClick={() => setFiltersModalOpen(false)}>
          Apply Filters
        </Button>
      </Modal>

      <Paginator
        pagination={pagination!}
        request={searchRequest}
        setRequest={(pag: any) => {
          setSearchRequest({ ...searchRequest, pageNumber: pag.pageNumber });
        }}
        defaultPageSize={parseInt(configs['CONF_DEF_ITEMS_PER_PAGE_CATALOG']?.value ?? '30')}
      />

      {/* Category bottom text  */}
      {category && category.bottomText && (
        <div className="md:col-span-4 mx-2">
          <div className="border-t border-solid border-[1px] border-gray-300 my-4"> </div>
          <div
            className=" text-md"
            dangerouslySetInnerHTML={{
              __html:
                category.bottomText
                  .replaceAll('{company}', configs['CONF_WEBSITE_NAME']?.value ?? '')
                  .replaceAll('{category}', category.name)
                  .replaceAll('{location}', location?.name ?? '') || '',
            }}
          ></div>
        </div>
      )}
    </div>
  ) : (
    <Spinner />
  );
};

export type PaginatorProps<T extends PaginationRequest> = {
  request?: T;
  setRequest?: (req: PaginationRequest) => void;
  pagination?: PaginationData;
  cypressPrevious?: string;
  cypressNext?: string;
  className?: string;
  defaultPageSize: number;
};

const Paginator = <SearchProductsRequest extends PaginationRequest>({
  pagination,
  setRequest,
  request,
  className,
  defaultPageSize,
}: PaginatorProps<SearchProductsRequest>) => {
  const setPage = (newPageNumber: number) => {
    if (request?.pageNumber !== newPageNumber) {
      setRequest?.({ ...request, pageNumber: newPageNumber });
    }
  };

  useEffect(() => {
    if (!request?.pageSize) {
      setRequest?.({ ...request, pageSize: defaultPageSize });
    }
  }, [request?.pageSize, defaultPageSize]);

  useEffect(() => {
    if (pagination?.lastPage && request?.pageNumber && request.pageNumber > pagination.lastPage) {
      setRequest?.({ ...request, pageNumber: pagination.lastPage });
    }
  }, [pagination?.lastPage]);

  return (
    <div className={classNames('paginator rounded-b-[12px] bg-white', className)}>
      <div className="md:min-w-[100px]">
        <Button
          id="button-prev-page"
          className="button-secondary"
          disabled={!pagination?.prevPage}
          onClick={() => setPage(pagination?.prevPage || 1)}
          data-cy="previous_page"
        >
          <i className="fa-solid fa-chevron-left sm-hide md:inline"></i>
          <i className="fa-solid fa-arrow-left inline md:hidden"></i>
          <span className="sm-hide md:inline">Previous</span>
        </Button>
      </div>
      {pagination && (
        <>
          {/* Tablet/desktop view */}
          <div className="visible-pages sm-hide md:flex">
            <Button
              className={`text-black ${classNames('button-tertiary-paginator', {
                active: 1 === pagination.pageNumber,
              })}`}
              onClick={() => setPage(1)}
            >
              1
            </Button>
            {pagination.pageNumber > 3 && <div className="ellipsis">...</div>}
            {Array.from(Array(5).keys())
              .map((offset) => offset - 2)
              .filter(
                (offset) => pagination.pageNumber + offset > 1 && pagination.pageNumber + offset < pagination.lastPage,
              )
              .map((offset) => (
                <Button
                  key={`page_${offset}`}
                  className={`text-black ${classNames('button-tertiary-paginator', {
                    active: offset === 0,
                  })}`}
                  onClick={() => setPage(pagination.pageNumber + offset)}
                >
                  {pagination.pageNumber + offset}
                </Button>
              ))}
            {pagination.pageNumber < pagination.lastPage - 3 && <div className="ellipsis">...</div>}
            {pagination.lastPage > 1 && (
              <Button
                id="button-last-page"
                className={classNames('text-black button-tertiary-paginator', {
                  active: pagination.lastPage === pagination.pageNumber,
                })}
                onClick={() => setPage(pagination.lastPage)}
              >
                {pagination.lastPage}
              </Button>
            )}
          </div>

          {/* Mobile view */}
          <div className="inline md-hide">
            <Typography type="text-sm" className="text-grey-700">
              Page {pagination.pageNumber} of {pagination.lastPage}
            </Typography>
          </div>
        </>
      )}

      <div className="md:min-w-[100px]">
        <Button
          className="button-secondary float-right"
          disabled={!pagination?.nextPage}
          onClick={() => setPage(pagination?.nextPage || 1)}
          data-cy="next_page"
        >
          <span className="sm-hide md:inline">Next</span>
          <i className="fa-solid fa-chevron-right sm-hide md:inline"></i>
          <i className="fa-solid fa-arrow-right inline md-hide"></i>
        </Button>
      </div>
    </div>
  );
};

const FiltersContent = ({
  categoryFilters,
  appliedFilters,
  setAppliedFilters,
  className,
}: {
  categoryFilters: FilterGroup[];
  appliedFilters: number[];
  setAppliedFilters: (filterIds: number[]) => void;
  className?: string;
}) => {
  return (
    <Container className={`filters ${className}`}>
      {categoryFilters.map((filterGroup) => {
        return (
          <div className="mb-4" key={filterGroup.id}>
            <h2>{filterGroup.name}</h2>
            {filterGroup.filters.map((filter) => {
              return (
                <div className="my-1 ml-2" key={filter.id}>
                  <Checkbox
                    label={`${filter.name} (${filter.productCount})`}
                    isChecked={appliedFilters.indexOf(filter.id) > -1}
                    onChange={(checked) =>
                      checked
                        ? setAppliedFilters(appliedFilters.concat(filter.id))
                        : setAppliedFilters(appliedFilters.filter((f) => f !== filter.id))
                    }
                  />
                </div>
              );
            })}
          </div>
        );
      })}
    </Container>
  );
};
